
import React from 'react';
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Progress } from "@/components/ui/progress";
import { 
  AlertTriangle, 
  Shield, 
  ArrowUp, 
  ArrowDown, 
  Calendar,
  FileText
} from 'lucide-react';
import { Button } from "@/components/ui/button";
import { Separator } from "@/components/ui/separator";

export interface TriggerDetailProps {
  trigger: {
    id: number;
    name: string;
    riskLevel: 'high' | 'medium' | 'low';
    description: string;
    trigger: string;
    risk: string;
    preventiveAction: string;
    category: string;
    status: 'active' | 'resolved' | 'monitoring';
    lastChecked: string;
    complianceScore: number;
    contextData?: {
      impactAreas?: string[];
      historicalTrends?: {
        period: string;
        score: number;
      }[];
      regulatoryReferences?: {
        name: string;
        description: string;
        link?: string;
      }[];
      recommendedActions?: {
        action: string;
        priority: 'critical' | 'high' | 'medium' | 'low';
        effort: 'small' | 'medium' | 'large';
        responsibleTeam: string;
      }[];
      relatedDocuments?: {
        name: string;
        type: string;
        lastUpdated: string;
      }[];
    };
  };
  onClose: () => void;
}

export const TriggerDetailView: React.FC<TriggerDetailProps> = ({ trigger, onClose }) => {
  const getRiskBadgeColor = (risk: string) => {
    switch (risk) {
      case 'high':
        return 'bg-red-100 text-red-800';
      case 'medium':
        return 'bg-yellow-100 text-yellow-800';
      case 'low':
        return 'bg-green-100 text-green-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  const getStatusBadgeVariant = (status: string) => {
    switch (status) {
      case 'active':
        return 'bg-orange-100 text-orange-800';
      case 'resolved':
        return 'bg-green-100 text-green-800';
      case 'monitoring':
        return 'bg-blue-100 text-blue-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  return (
    <div className="space-y-6 animate-fade-in">
      <div className="flex justify-between items-start">
        <div>
          <div className="flex items-center gap-2">
            <h2 className="text-2xl font-bold">{trigger.name}</h2>
            <div className={`inline-block px-2 py-1 rounded-full text-xs font-medium ${getRiskBadgeColor(trigger.riskLevel)}`}>
              {trigger.riskLevel.charAt(0).toUpperCase() + trigger.riskLevel.slice(1)} Risk
            </div>
            <div className={`inline-block px-2 py-1 rounded-full text-xs font-medium ${getStatusBadgeVariant(trigger.status)}`}>
              {trigger.status.charAt(0).toUpperCase() + trigger.status.slice(1)}
            </div>
          </div>
          <p className="text-muted-foreground mt-1">{trigger.description}</p>
        </div>
        <Button variant="outline" onClick={onClose}>Back to All Triggers</Button>
      </div>

      <div className="grid gap-6 md:grid-cols-3">
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-lg">Risk Factor</CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-sm">{trigger.risk}</p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-lg">Trigger Condition</CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-sm">{trigger.trigger}</p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-lg">Preventive Action</CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-sm">{trigger.preventiveAction}</p>
          </CardContent>
        </Card>
      </div>

      {trigger.contextData?.impactAreas && (
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-lg">Impact Areas</CardTitle>
          </CardHeader>
          <CardContent className="flex gap-2 flex-wrap">
            {trigger.contextData.impactAreas.map((area, index) => (
              <Badge key={index} variant="outline">{area}</Badge>
            ))}
          </CardContent>
        </Card>
      )}

      {trigger.contextData?.historicalTrends && trigger.contextData.historicalTrends.length > 0 && (
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-lg">Historical Compliance Score Trends</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-3">
              {trigger.contextData.historicalTrends.map((trend, index) => (
                <div key={index} className="flex items-center justify-between">
                  <span className="text-sm">{trend.period}</span>
                  <div className="flex items-center">
                    <span className={`mr-2 font-medium ${
                      trend.score >= 90 ? 'text-green-600' :
                      trend.score >= 80 ? 'text-yellow-600' : 'text-red-600'
                    }`}>{trend.score}%</span>
                    <Progress 
                      value={trend.score} 
                      className={`h-2 w-32 ${
                        trend.score >= 90 ? 'bg-green-100' :
                        trend.score >= 80 ? 'bg-yellow-100' : 'bg-red-100'
                      }`} 
                    />
                    {trend.score > trigger.complianceScore ? (
                      <ArrowDown className="ml-2 text-red-500 h-4 w-4" />
                    ) : trend.score < trigger.complianceScore ? (
                      <ArrowUp className="ml-2 text-green-500 h-4 w-4" />
                    ) : null}
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {trigger.contextData?.regulatoryReferences && trigger.contextData.regulatoryReferences.length > 0 && (
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-lg">Regulatory References</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              {trigger.contextData.regulatoryReferences.map((ref, index) => (
                <div key={index}>
                  <h4 className="font-medium">{ref.name}</h4>
                  <p className="text-sm text-muted-foreground">{ref.description}</p>
                  {ref.link && (
                    <a href={ref.link} className="text-sm text-blue-600 hover:underline">View regulation</a>
                  )}
                  {index < trigger.contextData!.regulatoryReferences!.length - 1 && (
                    <Separator className="my-2" />
                  )}
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {trigger.contextData?.recommendedActions && trigger.contextData.recommendedActions.length > 0 && (
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-lg">Recommended Actions</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              {trigger.contextData.recommendedActions.map((action, index) => (
                <div key={index} className="border-l-4 pl-4 pb-2 border-l-primary">
                  <div className="flex justify-between items-start mb-1">
                    <h4 className="font-medium">{action.action}</h4>
                    <div className={`inline-block px-2 py-1 rounded-full text-xs font-medium ${
                      action.priority === 'critical' ? 'bg-red-100 text-red-800' :
                      action.priority === 'high' ? 'bg-orange-100 text-orange-800' :
                      action.priority === 'medium' ? 'bg-yellow-100 text-yellow-800' :
                      'bg-blue-100 text-blue-800'
                    }`}>
                      {action.priority.charAt(0).toUpperCase() + action.priority.slice(1)} Priority
                    </div>
                  </div>
                  <div className="flex items-center text-sm gap-4 text-muted-foreground">
                    <span>Effort: {action.effort.charAt(0).toUpperCase() + action.effort.slice(1)}</span>
                    <span>Team: {action.responsibleTeam}</span>
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {trigger.contextData?.relatedDocuments && trigger.contextData.relatedDocuments.length > 0 && (
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-lg">Related Documents</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-2">
              {trigger.contextData.relatedDocuments.map((doc, index) => (
                <div key={index} className="flex items-center justify-between p-2 border rounded-md hover:bg-muted cursor-pointer">
                  <div className="flex items-center">
                    <FileText className="h-4 w-4 mr-2 text-primary" />
                    <div>
                      <p className="font-medium">{doc.name}</p>
                      <p className="text-xs text-muted-foreground">{doc.type}</p>
                    </div>
                  </div>
                  <div className="flex items-center text-xs text-muted-foreground">
                    <Calendar className="h-3 w-3 mr-1" />
                    {doc.lastUpdated}
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      <div className="flex justify-end">
        <Button onClick={onClose}>Back to All Triggers</Button>
      </div>
    </div>
  );
};
